import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { EggProductionRecord, IsoDate } from '@henlytics/types'
import { api } from '../lib/api-client'
import { queryKeys } from '../lib/query-keys'
import { clientUuid } from '../lib/uuid'

export function useProduction(batchId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.production(batchId ?? ''),
    queryFn: ({ signal }) => api.get<EggProductionRecord[]>(`/production?batchId=${batchId}`, signal),
    enabled: Boolean(batchId),
  })
}

export interface NewProduction {
  batchId: string
  productionDate: IsoDate
  eggsProduced: number
  eggsBroken: number
  eggsDirty: number
  notes?: string | null
  clientUuid?: string
}

export function useRecordProduction(batchId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: NewProduction) =>
      api.post<EggProductionRecord>('/production', {
        ...input,
        clientUuid: input.clientUuid ?? clientUuid(),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.production(batchId) })
      void queryClient.invalidateQueries({ queryKey: queryKeys.reports })
    },
  })
}

/**
 * Correcting a day's egg count voids the row and inserts a replacement, so the
 * original entry stays visible in the ledger (§3.1).
 */
export function useVoidProduction(batchId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ rowId, replacement }: { rowId: string; replacement?: NewProduction }) =>
      api.post<{ voided: EggProductionRecord; replacement: EggProductionRecord | null }>(
        `/production/${rowId}/void`,
        replacement
          ? { replacement: { ...replacement, clientUuid: replacement.clientUuid ?? clientUuid() } }
          : {},
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.production(batchId) })
      void queryClient.invalidateQueries({ queryKey: queryKeys.reports })
    },
  })
}
